// Phase 6.D-4 — 레시피 / 원가계산 sub.
// 회사 모든 master 메뉴를 fetch + 각 메뉴의 recipes 를 useQueries 로 병렬 fetch
// → 클라이언트에서 cogs 계산 (computeRecipeCost — recipe.lossRate || ingredient.defaultLossRate fallback).
// 컬럼: 메뉴명·기본가·재료수·재료원가·이익률(%).

import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  menusControllerListMaster,
  recipesControllerList,
} from '@dvvb/sdk';

import {
  AlertBanner,
  EmptyState,
  TableHeader,
  TableRow,
  TableShell,
} from '../../../components/primitives';
import { unwrap, ApiCallError } from '../../../lib/envelope';
import type { MenuRow, RecipeRow } from '../../../types/api';
import { computeRecipeCost, useInventoryMaterials } from './_shared';

interface Envelope<T> {
  items: T[];
  count: number;
  limit: number;
  cursor: string | null;
  has_more: boolean;
}

const COLS = 'minmax(180px,1.6fr) 100px 80px 110px 100px';

export function SettingsRecipesCostRoute() {
  const inventoryQ = useInventoryMaterials();

  const menusQ = useQuery({
    queryKey: ['menus', 'master'],
    queryFn: async () => {
      const r = await menusControllerListMaster({
        query: { limit: '500' } as never,
      });
      return unwrap<Envelope<MenuRow>>(r);
    },
  });

  const menus = menusQ.data?.items ?? [];

  const recipesQs = useQueries({
    queries: menus.map((m) => ({
      queryKey: ['recipes', { menuId: m.id }],
      queryFn: async () => {
        const r = await recipesControllerList({
          path: { menuId: m.id },
          query: { limit: '200' } as never,
        });
        return unwrap<Envelope<RecipeRow>>(r);
      },
      enabled: !!m.id,
    })),
  });

  const inventory = inventoryQ.data?.items ?? [];
  const inventoryById = useMemo(
    () => new Map(inventory.map((i) => [i.id, i])),
    [inventory],
  );

  const rows = useMemo(() => {
    return menus.map((m, i) => {
      const recipes = recipesQs[i]?.data?.items ?? [];
      const { totalCost } = computeRecipeCost(recipes, inventoryById);
      const price = Number(m.unitPrice ?? 0);
      const margin = price > 0 ? ((price - totalCost) / price) * 100 : 0;
      return {
        menu: m,
        inventoryCount: recipes.length,
        totalCost,
        price,
        margin,
        loading: !!recipesQs[i]?.isLoading,
      };
    });
  }, [menus, recipesQs, inventoryById]);

  if (menusQ.isLoading || inventoryQ.isLoading) {
    return (
      <div style={{ padding: 24, color: 'var(--fg-3)', fontSize: 13 }}>
        원가 데이터를 불러오는 중…
      </div>
    );
  }
  if (menusQ.isError) {
    return (
      <AlertBanner
        tone="danger"
        message={`메뉴 목록을 불러오지 못했습니다: ${(menusQ.error as ApiCallError).message ?? '알 수 없는 오류'}`}
      />
    );
  }
  if (menus.length === 0) {
    return (
      <EmptyState
        icon="receipt"
        title="등록된 메뉴가 없습니다"
        description="[메뉴추가] 탭에서 회사 메뉴와 레시피를 등록하세요."
      />
    );
  }

  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-weak)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid var(--border-weak)',
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg-1)' }}>
          메뉴별 원가 ({menus.length}개)
        </div>
        <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 2 }}>
          재료 원가 = quantity × (1 + lossRate) × cost. lossRate 는 recipe override → ingredient 기본값 fallback.
        </div>
      </div>
      <div style={{ padding: 16 }}>
        <TableShell>
          <TableHeader columns={COLS}>
            <div>메뉴</div>
            <div style={{ textAlign: 'right' }}>기본가</div>
            <div style={{ textAlign: 'right' }}>재료수</div>
            <div style={{ textAlign: 'right' }}>재료원가</div>
            <div style={{ textAlign: 'right' }}>이익률</div>
          </TableHeader>
          {rows.map((r, i) => (
            <TableRow key={r.menu.id} columns={COLS} first={i === 0}>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 600,
                    color: 'var(--fg-1)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {r.menu.name}
                </div>
                <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>
                  {r.menu.categoryName ?? '카테고리 없음'}
                </div>
              </div>
              <div
                style={{
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                ₩{Math.round(r.price).toLocaleString('ko-KR')}
              </div>
              <div
                style={{
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                  color: r.inventoryCount === 0 ? 'var(--fg-3)' : 'var(--fg-1)',
                }}
              >
                {r.loading ? '…' : r.inventoryCount}
              </div>
              <div
                style={{
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {r.loading ? '…' : `₩${Math.round(r.totalCost).toLocaleString('ko-KR')}`}
              </div>
              <div
                style={{
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                  color:
                    r.price === 0
                      ? 'var(--fg-3)'
                      : r.margin < 30
                        ? 'var(--danger)'
                        : r.margin < 50
                          ? 'var(--warning)'
                          : 'var(--success)',
                  fontWeight: 600,
                }}
              >
                {r.price === 0 || r.loading
                  ? '—'
                  : `${r.margin.toFixed(1)}%`}
              </div>
            </TableRow>
          ))}
        </TableShell>
      </div>
    </div>
  );
}
